import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {validateActionItem} from '../../common/validator/actionValidator'
import {FEATURE_NAMESPACE} from '../../consts'
import {
  useAddActionAttachments,
  useDeleteActionAttachment,
  useEditAction,
  usePlantUpmId,
  useSectionById
} from '../../hooks'
import {ActionItem} from '../../types'
import {MoveQuestionToDone} from '../actionsCompleted/MoveQuestionToDone'
import {Attachments} from '../common'

import {ActionDialog} from './ActionDialog'

type Props = {
  open: boolean
  handleOpen: (open: boolean) => void
  actionItem: ActionItem
  showApprovers?: boolean
}

export const EditActionDialog: React.FC<Props> = ({
  open,
  handleOpen,
  actionItem,
  showApprovers
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const plantId = usePlantUpmId()
  const [showMoveQuesDialog, setShowMoveQuesDialog] = useState<boolean>(false)
  const {mutate: editAction, isPending} = useEditAction(({showMoveQuestToDone}) => {
    handleOpen(false)
    showMoveQuestToDone && setShowMoveQuesDialog(true)
  })
  const {mutate: addAttachments, isPending: uploading} = useAddActionAttachments()
  const {mutate: deleteAttachment} = useDeleteActionAttachment()
  const {data: section} = useSectionById(actionItem.section?.id)
  const questionAnswer = useMemo(
    () =>
      section?.topics
        .find((topic) => actionItem.topic?.id === topic.id)
        ?.questions?.find((question) => actionItem.questionId === question.id)?.submission?.answer,
    [section?.topics, actionItem.questionId, actionItem.topic?.id]
  )

  const handleSubmit = (item: Partial<ActionItem>) => {
    editAction({actionItem: item as ActionItem, questionAnswer})
  }

  const actionId = Number(actionItem.id)

  return (
    <>
      <ActionDialog
        open={open}
        handleOpen={handleOpen}
        actionItem={actionItem}
        onSave={handleSubmit}
        onValidate={validateActionItem}
        saving={isPending}
        title={t('editAction.heading')}
        showApprovers={showApprovers}
      >
        <Attachments
          attachedFiles={actionItem.attachments}
          onUpload={(files) => addAttachments({plantId, actionId, files})}
          onDelete={(attachment) => deleteAttachment({plantId, actionId, attachment})}
          isUploading={uploading}
          inputId="upload-action-attachment-input"
        />
      </ActionDialog>
      {showMoveQuesDialog && (
        <MoveQuestionToDone actionItem={actionItem} onClose={() => setShowMoveQuesDialog(false)} />
      )}
    </>
  )
}
